import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorBox, Loading } from '../components/Feedback';
import { TicketCard } from '../components/TicketCard';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { usePush } from '../lib/push';
import { useTickets } from '../lib/ticket-state';
import { useTicketRefresh } from '../lib/use-ticket-refresh';
import type { MobileTab, Ticket } from '../types';

function StatusDot({ color, pulse = false }: { color: string; pulse?: boolean }) {
  return <span className={pulse ? 'connection-dot connection-dot--pulse' : 'connection-dot'} style={{ background: color }} />;
}

function DashboardStat({ value, label, tone, to }: { value: number | null; label: string; tone: string; to: string }) {
  return <Link className="dashboard-stat" to={to}>
    <strong style={{ color: tone }}>{value ?? '—'}</strong>
    <span>{label}</span>
  </Link>;
}

function QuickAction({ to, label, icon }: { to: string; label: string; icon: ReactNode }) {
  return <Link className="quick-action" to={to}>
    <span className="quick-action__icon">{icon}</span>
    <strong>{label}</strong>
  </Link>;
}

export function DashboardPage() {
  const { user } = useAuth();
  const push = usePush();
  const { realtimeStatus, mergeMany } = useTickets();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Record<MobileTab, number | null>>({ new: null, in_progress: null, completed: null });
  const [freshTickets, setFreshTickets] = useState<Ticket[]>([]);
  const [activeTickets, setActiveTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const requestId = useRef(0);
  const refresh = useCallback(async (freshData = false) => {
    const current = ++requestId.current;
    try {
      const [fresh, active, completed] = await Promise.all([
        api.tickets('new', 1, 3, freshData),
        api.tickets('in_progress', 1, 3, freshData),
        api.tickets('completed', 1, 1, freshData),
      ]);
      if (current !== requestId.current) return;
      setError('');
      setCounts({ new: fresh.total, in_progress: active.total, completed: completed.total });
      setFreshTickets(fresh.items);
      setActiveTickets(active.items);
      mergeMany([...fresh.items, ...active.items, ...completed.items]);
    } catch (reason) {
      if (current !== requestId.current) return;
      setError(reason instanceof Error ? reason.message : 'Dashboardul nu a putut fi încărcat');
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [mergeMany]);
  useEffect(() => {
    void refresh();
    return () => { requestId.current++; };
  }, [refresh]);
  useTicketRefresh(refresh);

  const displayedFresh = freshTickets;
  const displayedActive = activeTickets;
  const displayedCounts = counts;
  const activeCount = displayedCounts.new !== null && displayedCounts.in_progress !== null ? displayedCounts.new + displayedCounts.in_progress : null;
  const connection = {
    connected: { label: 'Conectat', color: '#34c759', pulse: true },
    connecting: { label: 'Conectare...', color: '#ff9500', pulse: false },
    disconnected: { label: 'Deconectat', color: '#8e8e93', pulse: false },
    error: { label: 'Eroare conexiune', color: '#ff3b30', pulse: false },
  }[realtimeStatus];
  return <div className="page dashboard-page">
    <header className="dashboard-hero">
      <span className="dashboard-glow dashboard-glow--top" aria-hidden="true" />
      <span className="dashboard-glow dashboard-glow--bottom" aria-hidden="true" />
      <div className="dashboard-hero__row">
        <div>
          <p className="dashboard-greeting">Bună ziua,</p>
          <h1>{user?.name || user?.username || 'Operator'}</h1>
          <p className="dashboard-identity">Nova Prompt · {user?.id || '—'}</p>
        </div>
        <Link className="dashboard-logout" to="/modules">Acasă</Link>
      </div>
      <div className="dashboard-connectivity">
        <span className="glass-pill"><StatusDot color={connection.color} pulse={connection.pulse} />{connection.label}</span>
        <button className="glass-pill" type="button" disabled={push.busy || push.state === 'unsupported'} title={push.message || undefined} onClick={() => void (push.state === 'enabled' ? push.disable() : push.enable())}><StatusDot color={push.state === 'enabled' ? '#34c759' : push.state === 'denied' || push.state === 'error' ? '#ff3b30' : '#8e8e93'} />{push.busy ? 'Se configurează…' : push.state === 'enabled' ? 'Notificări active' : push.state === 'denied' ? 'Notificări blocate' : 'Activează notificările'}</button>
      </div>
    </header>

    <div className="dashboard-body">
      <section className="dashboard-stats">
        <DashboardStat value={activeCount} label="Active" tone="var(--red)" to="/tickets?tab=new" />
        <DashboardStat value={displayedCounts.in_progress} label="În lucru" tone="var(--blue)" to="/tickets?tab=in_progress" />
        <DashboardStat value={displayedCounts.completed} label="Finalizate" tone="var(--green)" to="/tickets?tab=completed" />
      </section>

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && displayedActive.length > 0 && <section className="dashboard-section">
        <div className="dashboard-section__title">INTERVENȚIE ACTIVĂ</div>
        {displayedActive.map((ticket) => <TicketCard key={`${ticket.ticketId}:${ticket.runId || ''}`} ticket={ticket} />)}
      </section>}

      {!loading && displayedFresh.length > 0 && <section className="dashboard-section">
        <div className="dashboard-section__head">
          <div className="dashboard-section__title">DE PRELUAT · {displayedCounts.new ?? '—'}</div>
          {(displayedCounts.new === null || displayedCounts.new > 3) && <button type="button" onClick={() => navigate('/tickets?tab=new')}>Vezi toate</button>}
        </div>
        {displayedFresh.map((ticket) => <TicketCard key={`${ticket.ticketId}:${ticket.runId || ''}`} ticket={ticket} />)}
      </section>}

      {!loading && !error && activeCount === 0 && <div className="empty dashboard-empty"><strong>Totul în ordine</strong><span>Nu ai tichete active în acest moment.</span></div>}

      <section className="dashboard-section dashboard-quick-section">
        <div className="dashboard-section__title">ACȚIUNI RAPIDE</div>
        <div className="quick-actions">
          <QuickAction to="/stock" label="Transfer stoc" icon={<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 8 4v10l-8 4-8-4V7l8-4Z" /><path d="m4 7 8 4 8-4M12 11v10" /></svg>} />
          <QuickAction to="/reports" label="Rapoarte" icon={<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-6M22 20H2" /></svg>} />
        </div>
      </section>
    </div>
  </div>;
}
